import {
  expressionDefinitionApi,
  formBundleApi,
  formBundlesApi,
  formsExpressionDefinitionApi,
} from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { FeatureFlag } from 'constants/featureFlags';
import { SKIP_FORBIDDEN_RESPONSE_CONFIG } from 'constants/http';
import { catchAsyncActionError } from 'utils/action';
import { getConditionalObject } from 'utils/data';
import { getFormattedFormBundleData } from 'utils/forms';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';
import { selectFeatureFlagByKey } from 'store/global/selectors';

const formBundlesSchema = new schema.Entity('data', {}, { idAttribute: 'formBundleId' });

export const FORM_BUNDLE_ACTION_TYPE = createNamedActions('formBundle', {
  EDIT_BUNDLE: 'EDIT_BUNDLE',
  DELETE_BUNDLE: 'DELETE_BUNDLE',
  GET_BUNDLES: 'GET_BUNDLES',
  CREATE_BUNDLE: 'CREATE_BUNDLE',
  GET_BUNDLE_HISTORY: 'GET_BUNDLE_HISTORY',
  GET_BUNDLE_SCHEMA: 'GET_BUNDLE_SCHEMA',
});

export const editFormBundleAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await formBundlesApi.editFormBundle(params);

  return dispatch({
    type: FORM_BUNDLE_ACTION_TYPE.EDIT_BUNDLE,
    payload: getFormattedFormBundleData(data),
  });
});

export const createFormBundleOverrideAction = catchAsyncActionError(body => async dispatch => {
  const { data } = await formBundlesApi.createFormBundleOverride(body);

  return dispatch({
    type: FORM_BUNDLE_ACTION_TYPE.EDIT_BUNDLE,
    payload: getFormattedFormBundleData(data),
  });
});

export const deleteFormBundleAction = catchAsyncActionError(
  ({ formBundleId }) =>
    async dispatch => {
      await formBundlesApi.deleteFormBundle({ formBundleId });

      return dispatch({
        type: FORM_BUNDLE_ACTION_TYPE.DELETE_BUNDLE,
        payload: formBundleId,
      });
    },
);

export const getFormBundleAction = catchAsyncActionError(({ formBundleId }) => async () => {
  const { data } = await formBundlesApi.getFormBundle({ formBundleId });

  return data;
});

export const getFormBundlesAction = catchAsyncActionError(params => async dispatch => {
  const {
    includeExpressionValue = false,
    includeElements = false,
    isForbiddenSilent = false,
  } = params || {};

  const include = [
    ...(includeExpressionValue ? ['ExpressionValue'] : []),
    ...(includeElements ? ['Elements'] : []),
  ];

  const apiParams = {
    params: {
      include,
      allowDrafts: true,
    },
    paramsSerializer: { indexes: null },
    ...getConditionalObject(isForbiddenSilent, SKIP_FORBIDDEN_RESPONSE_CONFIG),
  };

  const { data } = await formBundlesApi.getFormBundles(apiParams);

  return dispatch({
    type: FORM_BUNDLE_ACTION_TYPE.GET_BUNDLES,
    payload: normalize(data.map(getFormattedFormBundleData), [formBundlesSchema]).entities.data,
  });
});

export const resetFormBundleOverrideAction = catchAsyncActionError(
  ({ formBundleId }) =>
    async dispatch => {
      await formBundlesApi.deleteFormBundle({ formBundleId });
      await dispatch(getFormBundlesAction());

      dispatch(showMessageAction({ message: 'Form bundle reset to original', variant: 'success' }));
    },
);

export const getFormBundleForLoanByLoanIdAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const isFormsConsolidationEnabled = selectFeatureFlagByKey(
      FeatureFlag.FormsConsolidationEnabled,
    )(getState());

    const { data } = isFormsConsolidationEnabled
      ? await formBundlesApi.getBundlesForLoan(params)
      : await formBundleApi.getBundleForLoan(params);

    const bundles = isFormsConsolidationEnabled ? data.map(getFormattedFormBundleData) : data;

    return dispatch({
      type: FORM_BUNDLE_ACTION_TYPE.GET_BUNDLES,
      payload: normalize(bundles, [formBundlesSchema]).entities.data,
    });
  },
);

export const createFormBundleAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await formBundlesApi.createFormBundle(params);

  return dispatch({
    type: FORM_BUNDLE_ACTION_TYPE.CREATE_BUNDLE,
    payload: getFormattedFormBundleData(data),
  });
});

export const copyFormBundleAction = catchAsyncActionError(
  ({ formBundleId, name }) =>
    async dispatch => {
      const { data } = await formBundlesApi.copyFormBundle({ formBundleId, name });

      return dispatch({
        type: FORM_BUNDLE_ACTION_TYPE.CREATE_BUNDLE,
        payload: getFormattedFormBundleData(data),
      });
    },
);

export const getFormBundleSchema = catchAsyncActionError(() => async (dispatch, getState) => {
  const isFormsConsolidationEnabled = selectFeatureFlagByKey(FeatureFlag.FormsConsolidationEnabled)(
    getState(),
  );

  const { data } = isFormsConsolidationEnabled
    ? await formsExpressionDefinitionApi.getFormBundleExpressionDefinition()
    : await expressionDefinitionApi.getFormBundleExpressionDefinition();

  return dispatch({
    type: FORM_BUNDLE_ACTION_TYPE.GET_BUNDLE_SCHEMA,
    payload: data,
  });
});
